<i18n>
    {
        "fa": {
            "alert": {
                "destination": "لطفا مقصد را انتخاب کنید",
                "checkIn": "لطفا تاریخ ورود را انتخاب کنید",
                "checkout": "لطفا تاریخ خروج را انتخاب کنید"
            }
        }
    }
</i18n>

<template>
    <div class="search-container row">
        <form @submit.prevent="submit" novalidate>
            <div class="gap" />

            <div class="col-xs-12 col-md-4" v-if="hasDestinationPicker">
                <hotel-destination-picker v-model="destination"
                                          :placeholder="$t('hotel.placeholders.destination')"
                                          :tabindex="1"
                                          ref="destination"
                >
                    <template slot="option" slot-scope="props">
                        {{ props.option ? optionNiceName(props.option) : '' }}
                    </template>
                    <template slot="tab" slot-scope="props">
                        <a href="#" @click.prevent="props.onTabChange(props.tab)" class="text-nowrap">
                            {{ props.tab }}
                            <small v-if="props.tab === 'هتل‌های داخلی'" class="badge badge--red">جدید</small>
                        </a>
                    </template>
                </hotel-destination-picker>
            </div><!-- /Origin & /Switch & /Destination -->

            <div class="col-xs-12" :class="{ 'col-md-7': !hasDestinationPicker, 'col-md-3': hasDestinationPicker}">
                <date-picker ref="datepicker" v-model="datePickerValue" :jalaali.sync="isJalaali" :min-range="1" range>
                    <div class="input-pair xs" slot-scope="{ open, focus, value }">
                        <div class="input-group" :class="{ focus: focus === 0 }" data-test="departingDatePicker">
                            <span class="input-group-addon" @click="open(0)"><i class="icon icon-calendar" /></span>
                            <input :value="value[0] ? value[0].format(isJalaali ? 'jYYYY/jMM/jDD' : 'YYYY/MM/DD') : null"
                                   placeholder="ورود"
                                   class="form-control"
                                   tabindex="2"
                                   readonly
                                   @click="open(0)"
                            >
                        </div>
                        <div class="input-group" :class="{ focus: focus === 1 }" data-test="returningDatePicker">
                            <span class="input-group-addon" @click="open(1)"><i class="icon icon-calendar" /></span>
                            <input :value="value[1] ? value[1].format(isJalaali ? 'jYYYY/jMM/jDD' : 'YYYY/MM/DD') : null"
                                   placeholder="خروج"
                                   class="form-control"
                                   tabindex="3"
                                   readonly
                                   @click="open(1)"
                            >
                        </div>
                    </div>

                    <template slot="day" slot-scope="props" v-if="$device.desktop">
                        <span v-tooltip="tooltipOptions(props)" class="calendar__day__content">{{ props.day.format(isJalaali ? 'jD' : 'D') }}</span>
                    </template>

                    <template slot="date-text" slot-scope="{ index }">
                        {{ index === 0 ? 'تاریخ ورود' : 'تاریخ خروج' }}
                    </template>
                    <template slot="after-date-text" v-if="nightsCount">
                        ({{ nightsCount }} شب)
                    </template>
                </date-picker>
            </div>

            <div class="col-xs-12 col-md-3">
                <hotel-passenger-picker v-model="rooms" :tabindex="4" ref="passengers" />
            </div><!-- /Passengers -->

            <div class="col-xs-12 col-md-2">
                <search-btn class="domestic-search" :disabled="!searchParamsChanged && isSearchLoading" :loading="isSearchLoading" :tabindex="5" />
            </div><!-- /Submit Button -->
        </form>
    </div>
</template>

<script>
// node_modules
import { mapActions, mapState, mapMutations } from 'vuex';
import { required } from 'vuelidate/lib/validators';
// mixins
import removeCookie from '~/scripts/mixins/removeCookie';
// utils
import { convertToJD } from '~/scripts/utils';
import cloneDeep from 'lodash/cloneDeep';
// components
import HotelPassengerPicker from '~/components/pages/international-hotel/search/hotel-passenger-picker';
import HotelDestinationPicker from '~/components/pages/international-hotel/search/hotel-destination-picker';
import SearchBtn from '~/components/common/search/search-btn';
import SearchPanel from '~/components/common/search/search-panel';
import DatePicker from '~/components/common/date-picker/date-picker';

export default {
    components: {
        HotelPassengerPicker,
        HotelDestinationPicker,
        SearchBtn,
        DatePicker,
    },

    extends: SearchPanel,

    mixins: [
        removeCookie,
    ],
    props: {
        hasDestinationPicker: {
            type: Boolean,
            default: true,
        },
    },
    data () {
        return {
            departing: '', // Submitted to available results
            returning: '', // Submitted to available results
            destination: { // Submitted to available results
                name: '',
                id: '',
                type: '',
            },
            rooms: [ // Submitted to available results
                {
                    type: 'unspecified',
                    adults: [30, 30],
                    children: [],
                },
            ],
        };
    },
    computed: {
        ...mapState('international-hotel', [
            'isSearchLoading',
            'searchParamsChanged',
            'baseUrl',
        ]),

        datePickerValue: {
            get () {
                return [
                    this.departing ? this.$moment.utc(this.departing, 'YYYY/MM/DD') : null,
                    this.returning ? this.$moment.utc(this.returning, 'YYYY/MM/DD') : null,
                ];
            },

            set (x) {
                this.departing = x[0] ? x[0].format('YYYY/MM/DD') : null;
                this.returning = x[1] ? x[1].format('YYYY/MM/DD') : null;
            },
        },

        nightsCount () {
            const v = this.datePickerValue;
            if (v[0] && v[1]) {
                return v[1].diff(v[0], 'days');
            }
            return null;
        },
    },
    watch: {
        departing () {
            this.setSearchParamsChanged(true);
        },
        returning () {
            this.setSearchParamsChanged(true);
        },
        rooms: {
            handler () {
                this.setSearchParamsChanged(true);
            },
            deep: true,
        },
        destination: {
            handler () {
                this.setSearchParamsChanged(true);
            },
            deep: true,
        },
    },
    async mounted () {
        this.setInitialValues();
        const { destination, datepicker, passengers } = this.$refs;
        this.autoNextInput([destination, datepicker, passengers].filter(Boolean));
    },
    validations () {
        return {
            departing: {
                required,
            },
            returning: {
                required,
            },
            ...(this.hasDestinationPicker ? {
                destination: {
                    id: {
                        required,
                    },
                },
            } : {}),
        };
    },
    methods: {
        ...mapActions(['openAlert']),
        ...mapMutations('international-hotel', [
            'setSearchLoading',
            'setSearchData',
            'setSearchParamsChanged',
        ]),
        setInitialValues () {
            this.cleanCookies();
            const hotelSearchInfo =
                this.$route.name === 'index-hotel'
                    ? this.$storage.getLocalStorage('hotelSearchInfo')
                    : this.$store.state['international-hotel'];
            const passedDate = hotelSearchInfo && this.$moment.autoParse(hotelSearchInfo.departing).isBefore(this.$moment().startOf('day'));
            if (hotelSearchInfo && !passedDate) {
                const {
                    destination,
                    departing,
                    returning,
                    rooms,
                } = hotelSearchInfo;
                if (destination && Object.keys(destination).length > 0) {
                    this.destination = {
                        ...destination,
                        title: destination.name,
                    };
                }
                const storeRooms = cloneDeep(rooms);
                if (storeRooms.length !== 0) {
                    this.rooms = storeRooms.map((room) => ({
                        ...room,
                        children: room.children.map((child) => {
                            if (child.age) return child;
                            else return { age: child };
                        }),
                    }));
                }

                if (departing) {
                    this.departing = departing;
                }
                if (returning) {
                    this.returning = returning;
                }
            }
        },
        // Clean all confirmInfo Cookies
        cleanCookies () {
            this.removeBus();
            this.removeTrain();
            this.removeDomestic();
            this.removeInternational();
        },
        // Combines city and hotel names with their countries and cities and states
        optionNiceName (option) {
            if (option.hotelType === 'Hotel') {
                return (option.city)
                    ? ((option.country) ? `${option.name} - ${option.city} - ${option.country}` : `${option.name} - ${option.city}`)
                    : ((option.country) ? `${option.name} - ${option.country}` : `${option.name}`);
            } else if (option.hotelType === 'DomesticCity') {
                return (option.state) ? `${option.name} - ${option.state}` : `${option.name}`;
            } else {
                if (option.countryCode.toLowerCase() === 'ir') {
                    return (option.state) ? `${option.name} - ${option.state}` : `${option.name}`;
                } else {
                    return (option.country) ? `${option.name} - ${option.country}` : `${option.name}`;
                }
            }
        },
        // Check validations
        checkValidInput () {
            // Make Sure the User has Selected A Destination
            if (!this.destination.id && this.hasDestinationPicker) {
                this.openAlert(this.$t('alert.destination'));
                return false;
            }
            // Make Sure the User has Selected A Departing Date
            if (!this.departing) {
                // If user searched hotel and didn't set date go to hotel page directly
                if (this.destination.type === 'Hotel') {
                    this.openHotelId();
                    return false;
                }
                this.openAlert(this.$t('alert.checkIn'));
                this.$gtm.sendGTMEvent({
                    event: 'event',
                    eventCategory: 'Hotel monitoring',
                    eventAction: 'Hotel',
                    eventLabel: this.$t('alert.checkIn'),
                });
                return false;
            }
            // Make Sure the User has Selected A Returning Date
            if (!this.returning) {
                // If user searched hotel and didn't set date go to hotel page directly
                if (this.destination.type === 'Hotel') {
                    this.openHotelId();
                    return false;
                }
                this.openAlert(this.$t('alert.checkout'));
                this.$gtm.sendGTMEvent({
                    event: 'event',
                    eventCategory: 'Hotel monitoring',
                    eventAction: 'Hotel',
                    eventLabel: this.$t('alert.checkout'),
                });
                return false;
            }
            if (convertToJD(this.returning) < convertToJD(this.departing)) {
                this.openAlert('تاریخ خروج نباید پیش از تاریخ ورود باشد.');
                return false;
            }
            return true;
        },
        openHotelId () {
            const routerData = this.$router.resolve({
                path: `${this.baseUrl}/${this.destination.link}`,
            });
            // Open in new tab
            window.open(routerData.href, '_blank');
        },
        submit () {
            const submittedRooms = this.rooms.map(room => ({
                ...room,
                children: room.children.map(child => child.age),
            }));
            // Check if data inputs are valid or not
            if (!this.checkValidInput()) {
                return false;
            }
            this.$v.$touch();
            if (!this.$v.$invalid) {
                // Send search tracking event
                this.sendSearchTrackingEvent('Submission');
                // Trying to change the format of children
                // Current: Children : [{age: 2}, {age: 3}] -> target: [2, 3]
                // the room submitted to server
                this.setSearchData({
                    destination: this.destination,
                    returning: this.returning,
                    departing: this.departing,
                    rooms: submittedRooms,
                });

                // After submitting
                this.setSearchParamsChanged(false);
                this.setSearchLoading(true);
                this.$emit('create-new-search');
            } else {
                // Focus first field that has error
                this.$setTimeout(() => {
                    $('.has-error')
                        .first()
                        .focus();
                }, 500);
            }
            this.$storage.setLocalStorage('hotelSearchInfo', {
                destination: this.destination,
                returning: this.returning,
                departing: this.departing,
                rooms: submittedRooms,
            });
        },
        tooltipOptions ({ focus, classes, open, day }) {
            if (!open || classes.before) return false;
            const value = this.datePickerValue;
            if (value.filter(Boolean).length === 2) {
                if (!value.find(d => d && d.isSame(day, 'day'))) return false;
            }
            return {
                content: (classes.selected ? classes['selected-1'] : focus === 1) ? 'تاریخ خروج' : 'تاریخ ورود',
                delay: 0,
                placement: 'top',
                classes: 'alibaba-datepicker-tooltip',
            };
        },
        sendSearchTrackingEvent (label) {
            // temporarily disable search tracking events due to farid's requests
            if (label) return;
            // search tracking
            this.$gtm.sendGTMEvent({
                event: 'event',
                eventCategory: 'Search Tracking',
                eventAction: 'Hotel',
                eventLabel: label,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    @import '~vars';

    .search-container {

        /deep/ .multitab-picker .badge {
            border-radius: 3px;
            font-size: 0.8em;
        }
    }
</style>
